import {
  IChat,
  MessageEvent,
  MessageStatus,
  MessageType,
  getTimeFromMessageId,
  isChatNotifType,
  isMessageAction,
  sealFrame,
  handoverFrame,
} from "@stringf/services/chat";
import { IRoom } from "@stringf/services/types";
import type { RefusalFrame } from "@stringf/services/refusal";
import { RoomType } from "@stringf/services/room";
import { atom } from "jotai";
import { atomFamily } from "jotai/utils";
import { accountsAtom } from "./accounts";
import {
  INotification,
  NotificationType,
} from "@stringf/services/notifications";
import { menuAtom } from "./menu";
import { getItem, setItem, removeItem } from "@stringf/services/storage";
import { authStateAtom } from "./auth";
import { endSessionAtom } from "./session";
import { loopedInIdsAtom, noteLocalLoopedInAtom, setLoopedInAtom } from "./loops";
import { messageStore } from "./message-store";
import { isMessageStoreWriter } from "./store-writer";

export type WebSocketMessage =
  | string
  | ArrayBuffer
  | SharedArrayBuffer
  | Blob
  | ArrayBufferView;
export type SendMessage = (message: WebSocketMessage, keep?: boolean) => void;

export interface Room {
  room?: IRoom;
  roomId: string;
  lastMessage?: IChat;
  allMessages?: Array<IChat>;
  pendingMessages?: Array<IChat>;
  oldestMessageId?: bigint;
  lastMessageId?: bigint;
  fetchedAll?: boolean;
  newMessages: Array<IChat>;
  refresh?: number;
  shouldSync?: boolean;
  lastSeenMessageId?: bigint;
  unseenMessages?: number;
  /**
   * This session's own per-Loop pseudonym id, learned the first time one of
   * our own sent messages echoes back with a substituted `userId` (anonymous
   * Loop only — see `applyIncoming`). Undefined until then, and undefined
   * forever for any room where the server never does this substitution.
   */
  ownAnonUserId?: string;
  /**
   * Ids this account hid ("Delete for me") this session. The server's room row
   * keeps naming a hidden newest message, so the merge needs to know not to
   * take its preview back.
   */
  hiddenIds?: string[];
  /** The last typing ping: who, and when this device heard it. Never a message. */
  typing?: { userId: string; at: number };
  /**
   * Our room-scoped member id (`info.userId` — a pseudonym in an anonymous Knot), as
   * the list rows report it. `room` exists only for rooms opened this session (ADR
   * 0005), so without this a room never opened could not tell our own message from
   * another device from someone else's. Memory only: the lists reload on every start.
   */
  memberUserId?: string;
}

export interface SocketState {
  connected: boolean;
  sendMessage?: SendMessage;
}

export const socketAtom = atom<SocketState>({
  connected: false,
});

export type MatchEvent =
  | { type: 'found'; roomId: string }
  | { type: 'expired' }
  | null;

export const matchEventAtom = atom<MatchEvent>(null);

// Local-only unseen-message tracking (no server round trip — see .scratch/frontend-audit/issues/12-read-receipts.md).
// Persisted per room so the read state survives a reload; a fresh reload for a room
// we've never locally tracked still falls back to the server's room.info.unseenMessages.
interface PersistedUnseen {
  lastSeenMessageId: string;
  unseenMessages: number;
}

const unseenStorageKey = (roomId: string) => `unseen:${roomId}`;

function readPersistedUnseen(roomId: string): PersistedUnseen | undefined {
  const raw = getItem(unseenStorageKey(roomId));
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as PersistedUnseen;
  } catch {
    return undefined;
  }
}

function persistUnseen(
  roomId: string,
  lastSeenMessageId: bigint | undefined,
  unseenMessages: number
) {
  setItem(
    unseenStorageKey(roomId),
    JSON.stringify({
      lastSeenMessageId: (lastSeenMessageId ?? BigInt(0)).toString(),
      unseenMessages,
    })
  );
}

// Local-only cache of this device's own per-Loop pseudonym id (see
// `Room.ownAnonUserId`). It is otherwise learned only by reconciling a sent
// message's echo, which means every message loaded from history *before* that
// reconciliation has happened this session has no way to be identified as
// ours. Persisting it means that gap only ever exists once per (device,
// anonymous Loop) — the first time this device ever sends into it — rather
// than resetting on every reload.
const ownAnonUserIdStorageKey = (roomId: string) => `ownAnonUserId:${roomId}`;

function readPersistedOwnAnonUserId(roomId: string): string | undefined {
  return getItem(ownAnonUserIdStorageKey(roomId)) ?? undefined;
}

function persistOwnAnonUserId(roomId: string, ownAnonUserId: string) {
  setItem(ownAnonUserIdStorageKey(roomId), ownAnonUserId);
}

// roomFamily is keyed by roomId string — single source of truth for per-room data
export const roomFamily = atomFamily(
  (roomId: string) => {
    const persisted = readPersistedUnseen(roomId);
    return atom<Room>({
      roomId,
      newMessages: [],
      lastSeenMessageId: persisted ? BigInt(persisted.lastSeenMessageId) : undefined,
      unseenMessages: persisted?.unseenMessages,
      ownAnonUserId: readPersistedOwnAnonUserId(roomId),
    });
  },
  (a, b) => a === b
);

/**
 * The room whose chat is open in this tab, or null. `Chat` sets it on mount and clears
 * it on unmount (only if it still names its own room). A message for it is not unread
 * while the tab is visible — mobile's `ChatService.activeRoomId`.
 */
export const activeRoomIdAtom = atom<string | null>(null);

/**
 * Our pseudonym in an anonymous Loop, from `/room/info` (`roomUserId`): recorded and
 * persisted like one learned from an echo, so after a reload our own messages from
 * another device are still ours. Wiped at sign-out with every `ownAnonUserId:*` key.
 */
export const rememberOwnAnonUserIdAtom = atom(null, (get, set, roomId: string, ownAnonUserId: string) => {
  if (!roomId || !ownAnonUserId) return;
  if (get(roomFamily(roomId)).ownAnonUserId !== ownAnonUserId) {
    set(roomFamily(roomId), (old) => ({ ...old, ownAnonUserId }));
  }
  if (readPersistedOwnAnonUserId(roomId) !== ownAnonUserId) persistOwnAnonUserId(roomId, ownAnonUserId);
});

/**
 * A Loop's own record, from its `/room/info`. A Loop is in no room list, so nothing
 * else fills `room` for it — and without the server's `lastMessageId` there,
 * `markRoomSeenAtom` cannot read past a newest message history never returns (one
 * hidden here with "Delete for me"), which would leave the Loop unread for good.
 * `lastMessageId` only moves forwards.
 */
export const rememberLoopAtom = atom(
  null,
  (get, set, loop: { id: string; isAnonymous?: boolean; lastMessageId?: unknown }) => {
    if (!loop.id) return;
    const room = get(roomFamily(loop.id)).room;
    const known = snowflake(room?.lastMessageId);
    const server = snowflake(loop.lastMessageId);
    const lastMessageId = server && (!known || server > known) ? server : known;
    if (room?.type === RoomType.LOOP && room.id === loop.id && known === lastMessageId) return;
    set(roomFamily(loop.id), (old) => ({
      ...old,
      room: {
        ...old.room,
        id: loop.id,
        type: RoomType.LOOP,
        isAnonymous: loop.isAnonymous ?? old.room?.isAnonymous,
        // The spec says number; at runtime ids are bigints (see `snowflake`).
        lastMessageId: lastMessageId as unknown as number,
      } as IRoom,
    }));
  }
);

/** Whether the person is looking at this tab right now. Outside a browser: yes. */
const tabVisible = () =>
  typeof document === "undefined" || document.visibilityState === "visible";

// markRoomSeenAtom: local-only "read" marker — advances the watermark to the newest
// message this device knows of (never backwards) and persists it. Never calls the
// server. A no-op when there is nothing new, so callers can fire it freely.
export const markRoomSeenAtom = atom(null, (get, set, roomId: string) => {
  const room = get(roomFamily(roomId));
  const candidates = [
    room.lastSeenMessageId,
    room.lastMessage?.messageId,
    snowflake(room.room?.lastMessageId),
  ].filter((id): id is bigint => id !== undefined && id > 0n);
  const lastSeenMessageId = candidates.length
    ? candidates.reduce((a, b) => (b > a ? b : a))
    : undefined;
  if (lastSeenMessageId === room.lastSeenMessageId && (room.unseenMessages ?? 0) === 0) return;
  set(roomFamily(roomId), (old) => ({ ...old, lastSeenMessageId, unseenMessages: 0 }));
  persistUnseen(roomId, lastSeenMessageId, 0);
});

export const notificationAtom = atom<{ newNotif: boolean }>({
  newNotif: false,
});

/**
 * Overlay this device's per-room runtime state onto one server row.
 *
 * React Query owns *which* rooms exist and their server fields; `roomFamily` owns
 * what only this device knows — accumulated messages, the local unseen watermark,
 * the per-Loop pseudonym. Neither is a subset of the other, so a list row is the
 * join of the two rather than a copy of either (ADR 0005).
 *
 * The room's metadata is reconciled by `updatedAt` rather than taken wholesale from
 * either side: react-query serves a list from cache for up to `staleTime`, so the
 * server row can legitimately be older than what `applyIncoming` has already
 * advanced over the live socket. The newer side wins, and the older side still fills
 * in fields it is the only source of — notably `info`, which carries membership and
 * the server's own unseen count and never arrives over the socket at all.
 *
 * The preview (`lastMessage`, `lastMessageId`, `lastMessageAt`) is reconciled
 * separately, by snowflake: the newest message this device holds overrides the
 * server row when its id is at least the server's `lastMessageId`. That holds for
 * every room, opened this session or not — `room` exists locally only for rooms
 * opened this session, which is why previews used to stall on every other row.
 */
export function mergeServerRoom(local: Room, server: IRoom): Room {
  const localAt = local.room?.updatedAt ? new Date(local.room.updatedAt).getTime() : -Infinity;
  const serverAt = server.updatedAt ? new Date(server.updatedAt).getTime() : -Infinity;
  const base = local.room && localAt > serverAt
    ? { ...server, ...local.room }
    : { ...local.room, ...server };
  const room: IRoom = {
    ...base,
    lastMessage: server.lastMessage,
    lastMessageId: server.lastMessageId,
    lastMessageAt: server.lastMessageAt,
  };
  const serverId = snowflake(server.lastMessageId);
  const serverHidden = !!serverId && !!local.hiddenIds?.includes(serverId.toString());
  const msg = local.lastMessage;
  if (msg && snowflake(msg.messageId) && (serverHidden || !serverId || msg.messageId >= serverId)) {
    Object.assign(room, previewFields(msg));
  } else if (serverHidden) {
    // Hidden here, and nothing older is known: say nothing rather than the hidden words.
    Object.assign(room, { lastMessage: undefined, lastMessageId: undefined });
  }
  return { ...local, roomId: server.id ?? local.roomId, room };
}

/**
 * A snowflake from whatever the wire handed over. `json-bigint` turns the server's
 * int64 ids into native bigints, but the spec types them `number`, and a fixture or a
 * small value can still be one. Zero and anything unparseable read as no id.
 */
export function snowflake(v: unknown): bigint | undefined {
  try {
    if (typeof v === "bigint") return v > 0n ? v : undefined;
    if (typeof v === "number" && Number.isFinite(v) && v > 0) return BigInt(Math.trunc(v));
    if (typeof v === "string" && /^\d+$/.test(v)) return BigInt(v) || undefined;
  } catch {
    // fall through
  }
  return undefined;
}

/** A room row's preview fields, as one message describes them. */
function previewFields(msg: IChat): Pick<IRoom, "lastMessage" | "lastMessageId" | "lastMessageAt"> {
  return {
    // A tombstone has no words: blank over a real id is how the list says "unsent".
    lastMessage: msg.unsentAt ? "" : msg.message,
    // The spec says number; at runtime ids are bigints (see `snowflake`).
    lastMessageId: msg.messageId as unknown as number,
    lastMessageAt: getTimeFromMessageId(msg.messageId).toISOString(),
  };
}

const timeOf = (iso?: string | null): number => {
  const ms = iso ? new Date(iso).getTime() : NaN;
  return Number.isFinite(ms) ? ms : 0;
};

/**
 * When this room was last active, in ms — the list's order and the row's time label.
 *
 * The later of the server's newest message (`lastMessageAt`, or the time encoded in
 * `lastMessageId`) and the newest message this device has seen. Message ids are
 * snowflakes, so the timestamp is already in the id: a live socket message floats its
 * room to the top whether or not the room was ever opened. Never the client clock.
 *
 * A room with no message at all falls back to when it was last edited or created.
 */
export function lastActivityAt(r: Room): number {
  const serverId = snowflake(r.room?.lastMessageId);
  const localId = snowflake(r.lastMessage?.messageId);
  const latest = Math.max(
    timeOf(r.room?.lastMessageAt),
    serverId ? getTimeFromMessageId(serverId).getTime() : 0,
    localId ? getTimeFromMessageId(localId).getTime() : 0
  );
  return latest || timeOf(r.room?.updatedAt) || timeOf(r.room?.createdAt);
}

/** Most recently active first. */
function byRecency(a: Room, b: Room): number {
  return lastActivityAt(b) - lastActivityAt(a);
}

/**
 * The rows for one room list: the caller's server page joined with local state and
 * ordered by recency.
 *
 * Ordering used to be an imperative reorder of an id list inside `chatEffect`. It is
 * derived here instead (`lastActivityAt`), so a room floats to the top because its
 * newest message actually moved — not because a write path remembered to move it.
 */
export function joinedRoomsAtom(serverRooms: IRoom[]) {
  return atom<Room[]>((get) =>
    serverRooms
      .filter((r) => !!r.id)
      .map((server) => mergeServerRoom(get(roomFamily(server.id!)), server))
      .sort(byRecency)
  );
}

/**
 * Whether any room in the caller's list has something unseen — the nav-tab dot.
 *
 * Takes the list as an argument rather than reading it from a store, so the badge is
 * correct as soon as the *query* has answered. It no longer depends on whether the
 * user happened to visit the page that used to populate the id list.
 */
export function unreadForRoomsAtom(serverRooms: IRoom[]) {
  const rooms = joinedRoomsAtom(serverRooms);
  return atom((get) => {
    const { user, anoUser } = get(authStateAtom);
    const currentUserId = user?.id || anoUser?.id;
    // The open room never lights its own section's dot, as it never lights its row.
    const active = get(activeRoomIdAtom);
    return get(rooms).some((r) => r.roomId !== active && isRoomUnread(r, currentUserId));
  });
}

/**
 * Whether one message is something the reader has not seen — the one rule behind the
 * unread count (`chatEffect`) and the badge (`isRoomUnread`). Mirrors mobile:
 *
 * - not ours, by any of our identities in this room: the account, the room-scoped
 *   member id (`info.userId`, a pseudonym in an anonymous Knot), a Loop's pseudonym.
 *   Our own message from another device is still ours;
 * - not a system event, an action, or a typing ping;
 * - newer than this device's read watermark.
 */
export function countsAsUnread(room: Room, msg: IChat, meId?: string): boolean {
  if (isMessageAction(msg.type) || isChatNotifType(msg.type) || isTypingFrame(msg)) return false;
  if (isOwnIdentity(room, msg.userId, meId)) return false;
  return !room.lastSeenMessageId || msg.messageId > room.lastSeenMessageId;
}

/**
 * `state` after a history page confirmed a pending send by client id: learns our
 * identity (the anonymous Loop pseudonym) and moves the read watermark, as the socket
 * echo does in `chatEffect`.
 */
export function learnOwnSend(roomId: string, state: Room, msg: IChat): Room {
  let next = state;
  if (msg.userId && msg.userId !== state.ownAnonUserId) {
    persistOwnAnonUserId(roomId, msg.userId);
    next = { ...next, ownAnonUserId: msg.userId };
  }
  if (!next.lastSeenMessageId || msg.messageId > next.lastSeenMessageId) {
    persistUnseen(roomId, msg.messageId, next.unseenMessages ?? 0);
    next = { ...next, lastSeenMessageId: msg.messageId };
  }
  return next;
}

/** Whether `userId` is one of our identities in this room. */
function isOwnIdentity(room: Room, userId: string, meId?: string): boolean {
  return (
    !!userId &&
    [meId, room.room?.info?.userId, room.memberUserId, room.ownAnonUserId].includes(userId)
  );
}

/**
 * Learn our member id in each room from the list rows (React Query owns them, ADR
 * 0005). Written only when it changes, so calling it on every list render is cheap.
 */
export const rememberMemberIdsAtom = atom(null, (get, set, serverRooms: IRoom[]) => {
  for (const r of serverRooms) {
    const memberUserId = r.info?.userId ?? undefined;
    if (!r.id || !memberUserId) continue;
    if (get(roomFamily(r.id)).memberUserId === memberUserId) continue;
    set(roomFamily(r.id), (old) => ({ ...old, memberUserId }));
  }
});

/** A server row's untyped `lastMessage` that is a system event's JSON payload. */
const isSystemPayload = (body?: string | null) => {
  const b = body?.trim() ?? "";
  if (!b.startsWith("{") || !b.endsWith("}")) return false;
  try {
    const p = JSON.parse(b);
    return !!p && typeof p === "object" && !Array.isArray(p);
  } catch {
    return false;
  }
};

/**
 * Whether the room has something the current user has not seen — the single source
 * for the row's dot, the finder and the nav-tab dot, so they can never disagree.
 *
 * Unread when this device counted something, or when the server row names a message
 * newer than this device's watermark that is not ours. A persisted local 0 cannot
 * hide that: it only says nothing *this device heard* is unread. With no watermark at
 * all (never opened here) the server's own count is all there is to go on.
 */
export function isRoomUnread(r: Room, currentUserId?: string): boolean {
  if ((r.unseenMessages ?? 0) > 0) return true;
  if ((r.newMessages ?? []).some((m) => countsAsUnread(r, m, currentUserId))) return true;
  const serverUnseen = (r.room?.info?.unseenMessages ?? 0) > 0;
  const watermark = r.lastSeenMessageId || undefined;
  if (watermark === undefined) return serverUnseen;
  const serverId = snowflake(r.room?.lastMessageId);
  if (!serverId || serverId <= watermark) return false;
  // The newest message, as this device holds it, says who wrote it.
  const local = r.lastMessage;
  if (local && local.messageId >= serverId) return countsAsUnread(r, local, currentUserId);
  if (isSystemPayload(r.room?.lastMessage)) return serverUnseen;
  return true;
}

/**
 * What the Loop feed knows about a Loop: its newest message, as the discovery row
 * names it. No sender — the row does not carry one.
 */
export interface LoopFeedRow {
  id: string;
  lastMessageId?: unknown;
  lastMessage?: string | null;
}

/**
 * Whether a Loop has something the user has not seen — its card's spine and dot, its
 * rail row, and (looped-in Loops only) the Loops tab.
 *
 * A Loop has no room list and no server unread count, so this is not `isRoomUnread`:
 *
 * - unread when this device counted something (`chatEffect`, the same rule as every
 *   room: not ours, not a system event, past the watermark, not the open room);
 * - also unread, for a Loop the user is looped into, when the feed row names a message
 *   newer than this device's watermark that is not ours. That is what survives a
 *   reload, when the counted messages are gone;
 * - never from the feed row alone without a watermark. A Loop has no membership, so a
 *   busy public Loop the user never read here would light up for nothing it could
 *   ever put out. Looping in seeds the watermark (`loopInLocallyAtom`).
 */
export function isLoopUnread(
  r: Room,
  { feed, loopedIn, meId }: { feed?: Omit<LoopFeedRow, "id">; loopedIn: boolean; meId?: string }
): boolean {
  if ((r.unseenMessages ?? 0) > 0) return true;
  if (!loopedIn) return false;
  const watermark = r.lastSeenMessageId || undefined;
  if (watermark === undefined) return false;
  const feedId = snowflake(feed?.lastMessageId);
  if (!feedId || feedId <= watermark) return false;
  // Hidden here ("Delete for me"): history never returns it, so nothing could read it.
  if (r.hiddenIds?.includes(feedId.toString())) return false;
  // The newest message, as this device holds it, says who wrote it.
  const local = r.lastMessage;
  if (local && local.messageId >= feedId) return countsAsUnread(r, local, meId);
  return !isSystemPayload(feed?.lastMessage);
}

/**
 * The Loops tab's dot: any Loop the user is looped into is unread (mobile's
 * `hasLoopBadge`). Takes the feed rows the caller has, for the reload case; a looped-in
 * Loop the feed has not paged in still counts by what this device heard. The open
 * Loop never lights it, as the open room never lights its section.
 */
export function loopsUnreadAtom(feed: readonly LoopFeedRow[]) {
  const byId = new Map(feed.filter((l) => !!l.id).map((l) => [l.id, l]));
  return atom((get) => {
    const { user, anoUser } = get(authStateAtom);
    const meId = user?.id || anoUser?.id;
    const active = get(activeRoomIdAtom);
    return get(loopedInIdsAtom).some(
      (id) =>
        id !== active &&
        isLoopUnread(get(roomFamily(id)), { feed: byId.get(id), loopedIn: true, meId })
    );
  });
}

/**
 * Loop in, on this device: the looped-in flag, and — when this device has never read
 * the Loop — a read watermark at the newest message known, so only what arrives after
 * counts. An existing watermark is never moved. No server call: the caller has either
 * made it (Loop in) or knows the server did (stringr loops the poster in on every
 * Loop message).
 */
export const loopInLocallyAtom = atom(
  null,
  (get, set, roomId: string, newestKnownId?: unknown) => {
    set(noteLocalLoopedInAtom, roomId);
    set(setLoopedInAtom, roomId, true);
    const room = get(roomFamily(roomId));
    if (room.lastSeenMessageId) return;
    const candidates = [
      room.lastMessage?.messageId,
      snowflake(room.room?.lastMessageId),
      snowflake(newestKnownId),
    ].filter((id): id is bigint => id !== undefined && id > 0n);
    if (!candidates.length) return;
    const lastSeenMessageId = candidates.reduce((a, b) => (b > a ? b : a));
    set(roomFamily(roomId), (old) => ({ ...old, lastSeenMessageId }));
    persistUnseen(roomId, lastSeenMessageId, room.unseenMessages ?? 0);
  }
);

const sameId = (a: bigint, b: bigint) => a.toString() === b.toString();

/**
 * Whether `m` carries the client id `id`. json-bigint leaves a small id a number, so a
 * wire id is compared by value. 0 and absent mean "no id" (a row stored before the server kept it).
 */
export const hasClientId = (m: IChat, id: bigint | undefined) =>
  !!id && !!m.clientMessageId && sameId(m.clientMessageId, id);

const tombstone = (m: IChat, at: number): IChat => ({
  ...m,
  message: "",
  url: undefined,
  fileUrl: undefined,
  unsentAt: m.unsentAt ?? at,
  reactions: undefined,
  myReaction: undefined,
  reactors: undefined,
});

/** One reactor's change folded into a message's counts: take the old emoji off, put the new on. */
function reactTo(m: IChat, identity: string, emoji: string | null, isMe: boolean): IChat {
  if (m.unsentAt) return m;
  // Counts alone come from history, so the old emoji is known only for us (myReaction)
  // or for a reactor already seen live.
  // ponytail: a REACTION replayed over a history page that already counted it adds
  // another reactor's first emoji twice; the who-reacted list is authoritative.
  const prev = isMe ? m.myReaction : m.reactors?.[identity];
  const counts = { ...m.reactions };
  if (prev && counts[prev]) {
    if (--counts[prev] <= 0) delete counts[prev];
  }
  if (emoji) counts[emoji] = (counts[emoji] ?? 0) + 1;
  const reactors = { ...m.reactors };
  if (emoji) reactors[identity] = emoji;
  else delete reactors[identity];
  return {
    ...m,
    reactions: Object.keys(counts).length ? counts : undefined,
    reactors: Object.keys(reactors).length ? reactors : undefined,
    myReaction: isMe ? emoji ?? undefined : m.myReaction,
  };
}

/**
 * Fold an UNSENT / REACTION / HIDDEN frame into the message it targets. These are
 * not messages: nothing is appended, and the preview and unread count stay put
 * (an unsent newest message blanks the preview, as the server does). A target this
 * device never loaded is a no-op, and UNSENT may repeat.
 *
 * `meId` is the signed-in account id; with `ownAnonUserId` it says which REACTION
 * frames are ours.
 */
function applyAction(state: Room, msg: IChat, meId?: string): Room {
  const target = (m: IChat) => sameId(m.messageId, msg.messageId);
  let edit: (m: IChat) => IChat;
  let hidden = false;
  if (msg.type === MessageType.Unsent) {
    const at = Date.now();
    edit = (m) => tombstone(m, at);
  } else if (msg.type === MessageType.Hidden) {
    hidden = true;
    edit = (m) => m;
  } else {
    try {
      const { identity, emoji, reactions } = JSON.parse(msg.message) as {
        identity: string;
        emoji?: string | null;
        reactions?: Record<string, number>;
      };
      // Our identity in this room: the account, an anonymous Loop's pseudonym, or
      // a Knot's per-room member id.
      const isMe =
        !!identity &&
        (identity === meId ||
          identity === state.ownAnonUserId ||
          identity === state.room?.info?.userId);
      edit = reactions
        ? // The frame carries the message's counts: take them as they are. Only our
          // own reaction says what myReaction is.
          (m) =>
            m.unsentAt
              ? m
              : {
                  ...m,
                  reactions: Object.keys(reactions).length ? reactions : undefined,
                  myReaction: isMe ? emoji ?? undefined : m.myReaction,
                }
        : (m) => reactTo(m, identity, emoji || null, isMe);
    } catch {
      return state;
    }
  }
  // Same array back when nothing matches, so effects keyed on it do not re-run.
  const apply = (list?: IChat[]) =>
    !list || !list.some(target)
      ? list
      : hidden
        ? list.filter((m) => !target(m))
        : list.map((m) => (target(m) ? edit(m) : m));

  const unsentPreview = msg.type === MessageType.Unsent && state.lastMessage && target(state.lastMessage);
  const allMessages = apply(state.allMessages);
  if (hidden) {
    // "Delete for me" of the newest message: the preview falls back to the newest
    // message still visible here. The server row keeps naming the hidden one, so it
    // is remembered for the merge (`mergeServerRoom`).
    const hidesPreview = !!state.lastMessage && target(state.lastMessage);
    const previous = hidesPreview
      ? [...(allMessages ?? [])]
          .reverse()
          .find((m) => m.messageId < msg.messageId && !isMessageAction(m.type) && !sealFrame(m))
      : undefined;
    const id = msg.messageId.toString();
    return {
      ...state,
      allMessages,
      newMessages: apply(state.newMessages) ?? state.newMessages,
      pendingMessages: apply(state.pendingMessages),
      hiddenIds: state.hiddenIds?.includes(id) ? state.hiddenIds : [...(state.hiddenIds ?? []), id],
      lastMessage: hidesPreview ? previous : state.lastMessage,
      room:
        hidesPreview && previous && state.room
          ? { ...state.room, ...previewFields(previous) }
          : state.room,
    };
  }
  return {
    ...state,
    allMessages,
    newMessages: apply(state.newMessages) ?? state.newMessages,
    pendingMessages: apply(state.pendingMessages),
    lastMessage: state.lastMessage && target(state.lastMessage) ? edit(state.lastMessage) : state.lastMessage,
    room:
      unsentPreview && state.room ? { ...state.room, lastMessage: "" } : state.room,
  };
}

/**
 * What an UNSENT / REACTION / HIDDEN frame does to a message held in the local store:
 * the in-memory reducer's own answer, so the two cannot disagree. `null` is hidden.
 */
function editStored(room: Room, stored: IChat, action: IChat, meId?: string): IChat | null {
  const only: Room = {
    ...room,
    allMessages: [stored],
    newMessages: [],
    pendingMessages: undefined,
    lastMessage: undefined,
  };
  return applyIncoming(only, action, meId).allMessages?.[0] ?? null;
}

/**
 * A typing ping (a frame in the SENDING status, which no stored message ever has) or
 * an empty frame. Neither is a message. System events and message actions are
 * never this, whatever their body.
 */
export function isTypingFrame(msg: IChat): boolean {
  if (isMessageAction(msg.type) || isChatNotifType(msg.type)) return false;
  // An Encrypted message this Device could not open has no text, and is still a message.
  if (msg.unreadable) return false;
  return msg.message === "" || msg.status === MessageStatus.SENDING;
}

/** A sync room's `events`, as the frames they replay; feed them to `chatEffect`. */
export const eventsToFrames = (roomId: string, events: MessageEvent[] = []): IChat[] =>
  events.map((e) => ({
    roomId,
    bucket: 0n,
    messageId: BigInt(e.messageId),
    userId: e.userId,
    message: e.body,
    type: e.type as MessageType,
  }));

/**
 * The server refused a message this client sent: its pending row fails, carrying
 * the code for the reason. A client id the room does not hold changes nothing.
 */
export function applyRefusal(state: Room, r: RefusalFrame): Room {
  const pending = state.pendingMessages ?? [];
  if (!pending.some((m) => hasClientId(m, r.clientMessageId))) return state;
  return {
    ...state,
    pendingMessages: pending.map((m) =>
      hasClientId(m, r.clientMessageId)
        ? { ...m, status: MessageStatus.FAILED, refusalCode: r.code }
        : m
    ),
  };
}

// applyIncoming: pure reducer — no atom access. Merges one incoming message into
// room state. Dedupes by messageId, acks optimistic messages by clientMessageId,
// falls back to text-match dedupe for messages without clientMessageId, resets on
// ClearedHistory, and merges room metadata on UpdatedRoom.
export function applyIncoming(state: Room, msg: IChat, meId?: string): Room {
  if (isMessageAction(msg.type)) return applyAction(state, msg, meId);
  // The seal is quiet: it moves the room's `sealedUntil` and leaves the history, the
  // preview and the unread count alone.
  const seal = sealFrame(msg);
  if (seal) return { ...state, room: { ...state.room, sealedUntil: seal.sealedUntil } as IRoom };
  let next = state;

  const handover = handoverFrame(msg);
  if (handover) {
    // `kind` and `admin` are not room fields: the frame only says who the admin is now.
    next = { ...next, room: { ...next.room, isAdmin: handover.admin === meId } as IRoom };
  } else if (msg.type === MessageType.UpdatedRoom) {
    try {
      const x: IRoom = JSON.parse(msg.message);
      next = { ...next, room: { ...next.room, ...x } as IRoom };
    } catch {}
  }

  // A message we sent, reconciled by the clientMessageId it was queued under.
  // In an anonymous Loop the server substitutes msg.userId with a per-Loop
  // pseudonym id before echoing it back (CONTEXT.md), even for our own
  // messages — so this is the only reliable way to learn what "our own
  // message" looks like on the wire, for the own/other bubble alignment.
  const isOwnMessage =
    (state.pendingMessages ?? []).some((m) => hasClientId(m, msg.clientMessageId));
  const ownAnonUserId = isOwnMessage ? msg.userId : next.ownAnonUserId;

  const allMessages =
    msg.type === MessageType.ClearedHistory
      ? [msg]
      : insertById(next.allMessages ?? [], msg);

  let pendingMessages: typeof next.pendingMessages;
  if (msg.clientMessageId) {
    pendingMessages = (next.pendingMessages ?? []).filter(
      (m) => !hasClientId(m, msg.clientMessageId)
    );
  } else {
    // text-fallback, for a legacy frame with no id and only when it is ours: another
    // user's "ok" must not swallow our lost "ok". Removes only the first pending
    // message with this text so that sending the same text twice keeps the second entry
    let removed = false;
    const ours = isOwnIdentity(next, msg.userId, meId);
    pendingMessages = (next.pendingMessages ?? []).filter((m) => {
      if (!removed && ours && m.message === msg.message) {
        removed = true;
        return false;
      }
      return true;
    });
  }

  const newMessages = [
    ...(next.newMessages ?? []).filter(
      (m) => m.messageId.toString() !== msg.messageId.toString()
    ),
    msg,
  ];

  // Only forwards: an older or replayed frame never moves the preview back. The
  // time comes from the snowflake, never the client clock.
  const advances = !next.lastMessage || msg.messageId >= next.lastMessage.messageId;
  const kept = allMessages.find((m) => sameId(m.messageId, msg.messageId)) ?? msg;
  return {
    ...next,
    lastMessage: advances ? kept : next.lastMessage,
    ownAnonUserId,
    room:
      next.room && advances
        ? {
            ...next.room,
            ...previewFields(kept),
            updatedAt: getTimeFromMessageId(msg.messageId).toISOString(),
          }
        : next.room,
    allMessages,
    pendingMessages,
    newMessages,
  };
}

/**
 * `list` with `msg` in id order. A message already there stays as it is: a replayed
 * frame carries neither reactions nor an unsend, and must not undo either.
 */
function insertById(list: IChat[], msg: IChat): IChat[] {
  if (list.some((m) => sameId(m.messageId, msg.messageId))) return list;
  let at = list.length;
  while (at > 0 && list[at - 1].messageId > msg.messageId) at--;
  return [...list.slice(0, at), msg, ...list.slice(at)];
}

/**
 * Fold incoming socket messages into per-room state. Synchronous by design.
 *
 * This used to fetch `room/get` for any room it had not seen before, because it also
 * had to know the room's *type* to file it into the right id list. Those lists are
 * gone (ADR 0005) and the room's metadata now comes from react-query, so there is
 * nothing left to await — a message for a room the list has not loaded yet still
 * lands in `roomFamily`, and `SocketListener` refreshes the list that is missing it.
 */
export const chatEffect = atom(null, (get, set, newMessages: IChat[]) => {
  const { user, anoUser } = get(authStateAtom);
  const meId = user?.id || anoUser?.id;
  newMessages.forEach((msg) => {
    const roomId = msg.roomId;

    if (isMessageAction(msg.type)) {
      const prev = get(roomFamily(roomId));
      set(roomFamily(roomId), (old) => applyIncoming(old, msg, meId));
      // The stored copy takes the same edit, loaded in memory or not.
      if (isMessageStoreWriter()) {
        void messageStore.update(roomId, msg.messageId, (stored) => editStored(prev, stored, msg, meId));
      }
      return;
    }

    // The seal moves the banner and the composer only: not stored, not a preview, not unread.
    if (sealFrame(msg)) {
      set(roomFamily(roomId), (prev) => applyIncoming(prev, msg, meId));
      return;
    }

    // Typing pings and empty frames are not messages: no bubble, no preview, no
    // unread. A ping only tells the open room someone is typing (chat.tsx).
    if (isTypingFrame(msg)) {
      // Only the open room shows typing; writing any other room would re-merge and
      // re-sort every list on every keystroke anywhere.
      if (msg.message !== "" && roomId === get(activeRoomIdAtom)) {
        set(roomFamily(roomId), (prev) => ({ ...prev, typing: { userId: msg.userId, at: Date.now() } }));
      }
      return;
    }

    // Being looked at: the open room, in a visible tab. A hidden tab is not reading.
    const viewing = roomId === get(activeRoomIdAtom) && tabVisible();

    // Single write path — applyIncoming handles all message types
    set(roomFamily(roomId), (prev) => {
      // Our own send, echoed back (matched by the clientMessageId it was queued under).
      const wasOwnPending =
        (prev.pendingMessages ?? []).some((m) => hasClientId(m, msg.clientMessageId));
      // A replay or a duplicate is the same message, not another unread one.
      const known = [...(prev.allMessages ?? []), ...(prev.newMessages ?? [])].some((m) =>
        sameId(m.messageId, msg.messageId)
      );
      const counts = !viewing && !wasOwnPending && !known && countsAsUnread(prev, msg, meId);
      const next = applyIncoming(prev, msg, meId);
      // Cache a freshly learned ownAnonUserId so it survives a reload — see
      // `readPersistedOwnAnonUserId`.
      if (next.ownAnonUserId && next.ownAnonUserId !== prev.ownAnonUserId) {
        persistOwnAnonUserId(roomId, next.ownAnonUserId);
      }
      if (counts) {
        const unseenMessages = (next.unseenMessages ?? 0) + 1;
        persistUnseen(roomId, next.lastSeenMessageId, unseenMessages);
        return { ...next, unseenMessages };
      }
      // Our own message — sent here, or from another device — is read by definition:
      // it moves the watermark (mobile's count floor). Otherwise, after a reload, the
      // server row would name a message newer than the watermark with no sender, and
      // the room would read as unread until opened.
      const ours =
        !isChatNotifType(msg.type) && (wasOwnPending || isOwnIdentity(prev, msg.userId, meId));
      if (ours && (!next.lastSeenMessageId || msg.messageId > next.lastSeenMessageId)) {
        persistUnseen(roomId, msg.messageId, next.unseenMessages ?? 0);
        return { ...next, lastSeenMessageId: msg.messageId };
      }
      return next;
    });

    // Keep what the room now holds for this frame: a replay of a message that was since
    // unsent or reacted to must not write the old copy back.
    // Only the leader tab writes: followers get the same frames, into the same database.
    if (isMessageStoreWriter()) {
      const state = get(roomFamily(roomId));
      if (msg.type === MessageType.ClearedHistory) {
        void messageStore.replaceRoom(roomId, [msg]);
      } else {
        const kept = state.allMessages?.find((m) => sameId(m.messageId, msg.messageId)) ?? msg;
        void messageStore.putMessages([kept]);
      }
    }

    // No id list to reorder: `applyIncoming` advanced this room's `lastMessage`, and
    // `joinedRoomsAtom` sorts on its snowflake, so the row floats to the top of every
    // list that contains it without a second write path having to remember to move it.
  });
});

export const refusalEffect = atom(null, (_get, set, refusals: RefusalFrame[]) => {
  // A refusal that names no room has no row to mark; don't open a room for "".
  refusals.forEach((r) => r.roomId && set(roomFamily(r.roomId), (old) => applyRefusal(old, r)));
});

// notifEffect: set menu dot and match events only.
// Cache prepend + friends invalidation happen in socket-hook.tsx (React context).
export const notifEffect = atom(
  null,
  (_, set, newNotifications: INotification[]) => {
    if (!newNotifications || newNotifications.length === 0) return;
    set(menuAtom, (menu) => ({ ...menu, notifs: { ...menu.notifs, notification: true } }));
    set(notificationAtom, { newNotif: true });
    const foundNotif = newNotifications.find(
      (n) => Number(n.notifType) === NotificationType.MATCH_FOUND
    );
    if (foundNotif?.sRoomId) {
      set(matchEventAtom, { type: 'found', roomId: foundNotif.sRoomId });
    } else if (
      newNotifications.some(
        (n) => Number(n.notifType) === NotificationType.MATCH_EXPIRED
      )
    ) {
      set(matchEventAtom, { type: 'expired' });
    }
  }
);

/**
 * Forget everything this device knows about a room — used when leaving one.
 *
 * It does not remove the room from any list, because no list lives here any more:
 * the caller invalidates the react-query rooms list, which is what decides whether
 * the room still exists for this user.
 */
export const forgetRoomAtom = atom(null, (_, __, room: { roomId: string }) => {
  void messageStore.forgetRoom(room.roomId);
  removeItem(unseenStorageKey(room.roomId));
  removeItem(ownAnonUserIdStorageKey(room.roomId));
  roomFamily.remove(room.roomId);
});

/**
 * Fills a room from the local message store, so a reload shows its messages before the
 * network answers. Only messages memory does not already hold are added: the server's
 * history and sync stay authoritative (`loadHistoryPage` overwrites what it returns).
 * Does not set `oldestMessageId`, so the first history page still loads.
 */
export const hydrateRoomAtom = atom(null, async (_, set, roomId: string) => {
  if (!roomId) return;
  // A sign-out (here or in another tab) while the read was out: it must not land.
  const epoch = messageStore.epoch();
  const stored = await messageStore.recentMessages(roomId);
  if (!stored.length || epoch !== messageStore.epoch()) return;
  set(roomFamily(roomId), (old) => {
    // History answered first: it knows better, and stored messages older than its page
    // may not be contiguous with it.
    if (old.oldestMessageId) return old;
    const have = new Set((old.allMessages ?? []).map((m) => m.messageId.toString()));
    const fresh = stored.filter((m) => !have.has(m.messageId.toString()));
    if (!fresh.length) return old;
    const allMessages = [...(old.allMessages ?? []), ...fresh].sort((a, b) =>
      a.messageId < b.messageId ? -1 : a.messageId > b.messageId ? 1 : 0
    );
    return { ...old, allMessages };
  });
});

export const clearAllSocket = atom(null, (_, set) => {
  void messageStore.wipe();
  set(accountsAtom, {});
  set(notificationAtom, { newNotif: false });
  for (const param of roomFamily.getParams()) {
    removeItem(unseenStorageKey(param));
    roomFamily.remove(param);
  }
  set(menuAtom, { notifs: {} });
  set(matchEventAtom, null);
});

/** The user signing out. One routine with the forced sign-out: `endSessionAtom`. */
export const logoutAtom = atom(null, (_, set) => set(endSessionAtom, "logout"));

// After a socket reconnect every open room may have missed messages. The set of
// rooms this device is actually tracking is roomFamily's own key set — walking that
// covers rooms in either list, and rooms in neither (a room open by direct URL).
export const updateSyncRooms = atom(null, (_, set) => {
  for (const roomId of roomFamily.getParams()) {
    set(roomFamily(roomId), (old) => ({ ...old, shouldSync: true }));
  }
});
